import "./App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Suspense } from "react";
import { ProtectedRoute } from "./components/protected-route";
import { UserRole } from "./utils/enum/user-role.enum";
import { PageLoader } from "./components/page-loader";
import { createLazyComponent } from "./utils/lazyLoad";
import { UserInviteVerifyPage } from "./page/user-invite-verify.page";

// Types for components with proper exports
type DocumentsPageProps = {
  baseUrl: string;
};

// Lazy load layouts with chunk naming
const LandingPageLayout = createLazyComponent(
  () =>
    import("./page/landing-layout").then((module) => ({
      default: module.LandingPageLayout,
    })),
  "layout-landing"
);

const DashboardLayout = createLazyComponent(
  () =>
    import("./page/dashboard-layout").then((module) => ({
      default: module.DashboardLayout,
    })),
  "layout-dashboard"
);

// Lazy load all pages with chunk naming
const LoginPage = createLazyComponent(
  () => import("./page/login"),
  "page-login"
);

const ServicesPage = createLazyComponent(
  () =>
    import("./page/services").then((module) => ({
      default: module.ServicesPage,
    })),
  "page-services"
);

const ContactUsPage = createLazyComponent(
  () =>
    import("./page/contact-us").then((module) => ({
      default: module.ContactUsPage,
    })),
  "page-contact"
);

const DocumentsPage = createLazyComponent<DocumentsPageProps>(
  () => import("./page/documents"),
  "page-documents"
);

const LandingPage = createLazyComponent(
  () => import("./page/landing"),
  "page-landing"
);
const DashboardPage = createLazyComponent(
  () => import("./page/dashboard"),
  "page-dashboard"
);
const CompaniesPage = createLazyComponent(
  () => import("./page/companies.page"),
  "page-companies"
);
const UsersPage = createLazyComponent(
  () => import("./page/users/users.page"),
  "page-users"
);
const PublicDocumentsPage = createLazyComponent(
  () => import("./page/public-documents.page"),
  "page-public-documents"
);
const ForgetPasswordPage = createLazyComponent(
  () => import("./page/forget-password.page"),
  "page-forget-password"
);
const ResetPasswordPage = createLazyComponent(
  () => import("./page/reset-password.page"),
  "page-reset-password"
);
const AccountSettingsPage = createLazyComponent(
  () => import("./page/account-settings.page"),
  "page-account-settings"
);

// Already lazy-loaded pages but with improved loading
// const LandingPage = () =>
//   lazyLoad(() => import("./page/landing"), <PageLoader />, "page-landing");

// const DashboardPage = () =>
//   lazyLoad(() => import("./page/dashboard"), <PageLoader />, "page-dashboard");

// const CompaniesPage = () =>
//   lazyLoad(
//     () => import("./page/companies.page"),
//     <PageLoader />,
//     "page-companies"
//   );

// const UsersPage = () =>
//   lazyLoad(
//     () => import("./page/users/users.page"),
//     <PageLoader />,
//     "page-users"
//   );

// const PublicDocumentsPage = () =>
//   lazyLoad(
//     () => import("./page/public-documents.page"),
//     <PageLoader />,
//     "page-public-documents"
//   );

// const ForgetPasswordPage = () =>
//   lazyLoad(
//     () => import("./page/forget-password.page"),
//     <PageLoader />,
//     "page-forget-password"
//   );

// const ResetPasswordPage = () =>
//   lazyLoad(
//     () => import("./page/reset-password.page"),
//     <PageLoader />,
//     "page-reset-password"
//   );

// const AccountSettingsPage = () =>
//   lazyLoad(
//     () => import("./page/account-settings.page"),
//     <PageLoader />,
//     "page-account-settings"
//   );

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route element={<LandingPageLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="services" element={<ServicesPage />} />
            <Route path="contact-us" element={<ContactUsPage />} />
            <Route
              path="documents"
              element={<DocumentsPage baseUrl="/documents" />}
            />
            <Route
              path="documents/:documentId"
              element={<DocumentsPage baseUrl="/documents" />}
            />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route path="dashboard" element={<DashboardLayout />}>
              <Route path="" element={<DashboardPage />} />

              <Route element={<ProtectedRoute role={[UserRole.SUPER_ADMIN]} />}>
                <Route path="companies" element={<CompaniesPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route
                  path="public-documents"
                  element={<PublicDocumentsPage />}
                />
              </Route>
              <Route
                path="account-settings"
                element={<AccountSettingsPage />}
              />
            </Route>
          </Route>
          <Route path="login" element={<LoginPage />} />
          <Route path="user/invite/:token" element={<UserInviteVerifyPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="forgot-password" element={<ForgetPasswordPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
